import { useEffect, useRef, useState } from 'react';
import { getCameraConfig } from '../../../lib/config/cameras';

const CAMERA_RESOLUTION_STORAGE_KEY = 'photobooth-camera-resolution';

function getSavedCameraResolution(): { width: number; height: number } | null {
  try {
    const raw = localStorage.getItem(CAMERA_RESOLUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { width?: number; height?: number };
    if (typeof parsed?.width === 'number' && typeof parsed?.height === 'number' && parsed.width > 0 && parsed.height > 0) {
      return { width: parsed.width, height: parsed.height };
    }
    return null;
  } catch {
    return null;
  }
}

function saveCameraResolution(width: number, height: number): void {
  try {
    localStorage.setItem(CAMERA_RESOLUTION_STORAGE_KEY, JSON.stringify({ width, height }));
  } catch {
    // ignore quota or disabled localStorage
  }
}

interface CameraPreviewProps {
  onCapture: (imageBlob: Blob) => void;
  shotNumber: number;
  totalShots: number;
  countdownSeconds?: number;
  onCountdownComplete?: () => void;
  /** When true, start countdown automatically (for continuous capture after first shot) */
  autoStartNextShot?: boolean;
  /** When provided, countdown value is reported here and overlay is not shown on video */
  onCountdownChange?: (value: number | null) => void;
  /** When true, keep camera mounted but invisible (e.g. during review so preview shows immediately after) */
  hidden?: boolean;
  /** Called when user clicks Start (first shot only, not auto-started next shots) */
  onStartClick?: () => void;
}

export function CameraPreview({
  onCapture,
  shotNumber,
  totalShots,
  countdownSeconds = 5,
  onCountdownComplete,
  autoStartNextShot = false,
  onCountdownChange,
  hidden = false,
  onStartClick,
}: CameraPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [countdown, setCountdown] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cameraResolution, setCameraResolution] = useState<{
    current: string;
    max: string;
  } | null>(null);

  // Browser camera: getUserMedia for preview
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera access is not supported in this browser.');
      setIsLoading(false);
      return;
    }

    let stream: MediaStream | null = null;

    function getMaxResolution(cap: {
      width?: number | { min?: number; max?: number };
      height?: number | { min?: number; max?: number };
    }): { width: number; height: number } | null {
      const maxW =
        typeof cap.width === 'object' && cap.width?.max != null
          ? cap.width.max
          : typeof cap.width === 'number'
            ? cap.width
            : null;
      const maxH =
        typeof cap.height === 'object' && cap.height?.max != null
          ? cap.height.max
          : typeof cap.height === 'number'
            ? cap.height
            : null;
      if (maxW != null && maxH != null && maxW > 0 && maxH > 0) return { width: maxW, height: maxH };
      return null;
    }

    const startCamera = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setCameraResolution(null);

        const { photoCameraDeviceId } = getCameraConfig();
        const deviceConstraint = photoCameraDeviceId ? { deviceId: { exact: photoCameraDeviceId } } : {};
        const mergeVideo = (base: MediaTrackConstraints) => ({ ...base, ...deviceConstraint });
        const videoFallback = photoCameraDeviceId ? { video: { deviceId: { exact: photoCameraDeviceId } } } : { video: true };

        // Probe: get a temporary stream to read camera max resolution (portrait: height > width)
        const probeStream = await navigator.mediaDevices.getUserMedia({
          video: mergeVideo({ width: { ideal: 2160 }, height: { ideal: 3840 }, aspectRatio: { ideal: 9/16 } }),
        });
        const probeTrack = probeStream.getVideoTracks()[0];
        let maxRes: { width: number; height: number } | null = null;
        if (probeTrack) {
          const cap = probeTrack.getCapabilities() as {
            width?: number | { min?: number; max?: number };
            height?: number | { min?: number; max?: number };
          };
          maxRes = getMaxResolution(cap);
          probeStream.getTracks().forEach((t) => t.stop());
        }

        // Prefer high resolution: use "ideal" so browser picks closest supported (avoids OverconstrainedError from "exact")
        const tryIdeal = (w: number, h: number) =>
          navigator.mediaDevices.getUserMedia({
            video: mergeVideo({ width: { ideal: w }, height: { ideal: h } }),
          });

        const savedRes = getSavedCameraResolution();
        if (savedRes) {
          try {
            stream = await tryIdeal(savedRes.width, savedRes.height);
          } catch {
            // saved dimensions no longer supported (e.g. different camera), fall through
          }
        }

        if (!stream && maxRes && maxRes.width > 480 && maxRes.height > 640) {
          try {
            stream = await tryIdeal(maxRes.height, maxRes.width);
          } catch {
            try {
              stream = await tryIdeal(maxRes.width, maxRes.height);
            } catch {
              stream = await navigator.mediaDevices.getUserMedia(videoFallback);
            }
          }
        }
        if (!stream) {
          // Reported max is low or no saved resolution: try common portrait resolutions (width, height) with ideal
          const ladder: [number, number][] = [
            [2160, 3840],
            [1080, 1920],
            [720, 1280],
          ];
          for (const [w, h] of ladder) {
            try {
              stream = await tryIdeal(w, h);
              break;
            } catch {
              continue;
            }
          }
          if (!stream) {
            stream = await navigator.mediaDevices.getUserMedia(videoFallback);
          }
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        const track = stream.getVideoTracks()[0];
        if (track) {
          const settings = track.getSettings();
          const curW = settings.width ?? 0;
          const curH = settings.height ?? 0;
          const cap = track.getCapabilities() as {
            width?: number | { min?: number; max?: number };
            height?: number | { min?: number; max?: number };
          };
          const max = getMaxResolution(cap);
          setCameraResolution({
            current: `${curW}×${curH}`,
            max: max ? `${max.width}×${max.height}` : `${curW}×${curH}`,
          });
          saveCameraResolution(max?.width ?? 0, max?.height ?? 0);
        }
        setIsReady(true);
      } catch (err) {
        const ex = err as { name?: string; message?: string };
        if (ex.name === 'NotAllowedError') {
          setError('Camera access was denied. Please allow camera in your browser.');
        } else if (ex.name === 'NotFoundError') {
          setError('No camera found.');
        } else {
          setError(ex.message || 'Failed to start camera');
        }
        setCameraResolution(null);
        setIsReady(false);
      } finally {
        setIsLoading(false);
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  const startCountdown = () => {
    if (!autoStartNextShot) {
      onStartClick?.();
    }
    setCountdown(countdownSeconds);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(countdownInterval);
          capturePhoto();
          onCountdownComplete?.();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Report countdown to parent when provided
  useEffect(() => {
    onCountdownChange?.(countdown);
  }, [countdown, onCountdownChange]);

  // Auto-start next shot countdown when returning from review (no second click needed)
  useEffect(() => {
    if (autoStartNextShot && isReady && !error && countdown === null && !hidden) {
      startCountdown();
    }
  }, [autoStartNextShot, isReady, error, countdown, hidden]);

  // Enter key triggers same action as Start button when button would be visible
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (!isReady || error || countdown !== null || autoStartNextShot || hidden) return;
      e.preventDefault();
      startCountdown();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isReady, error, countdown, autoStartNextShot, hidden]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !streamRef.current || video.videoWidth === 0) {
      setError('Camera not ready for capture');
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Could not get canvas context');
      return;
    }
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onCapture(blob);
        } else {
          setError('Failed to capture image');
        }
      },
      'image/jpeg',
      0.95
    );
  };

  return (
    <div className={`relative ${hidden ? 'absolute inset-0 invisible pointer-events-none overflow-hidden' : ''}`}>
      <div className="relative aspect-[3/4] bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {isLoading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
            <div className="text-center text-white">
              <div className="text-6xl mb-4">📸</div>
              <p className="text-lg">Connecting to camera...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-900/80">
            <div className="text-center text-white p-6">
              <div className="text-6xl mb-4">⚠️</div>
              <p className="text-lg font-semibold mb-2">Camera Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {countdown !== null && !onCountdownChange && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="text-9xl font-bold text-white animate-pulse">
              {countdown}
            </div>
          </div>
        )}

        {!onCountdownChange && (
          <div className="absolute top-4 right-4 px-4 py-2 bg-black/70 rounded-full text-white font-semibold">
            Shot {shotNumber} / {totalShots}
          </div>
        )}
        {cameraResolution && (
          <div className="absolute bottom-4 left-4 right-4 text-center text-white text-sm font-medium drop-shadow-md">
            Current: {cameraResolution.current} · Max: {cameraResolution.max}
          </div>
        )}
      </div>

      {isReady && !error && countdown === null && !autoStartNextShot && !hidden && (
        <div className="mt-6 text-center">
          <button
            onClick={startCountdown}
            className="px-12 py-6 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white text-2xl font-bold shadow-xl hover:shadow-2xl transition-all hover:scale-105 active:scale-95"
          >
            Start
          </button>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
